import { useMemo, useState } from "react";
import { AtSign, BellOff, Phone, Pin, Plus, Search } from "lucide-react";
import type { Room } from "./types";
import { Avatar, Empty, useChat } from "./ui";
import { listTime } from "./jalali";

interface Props { activeId: number | null; onOpen: (id: number) => void; onNew: () => void }
type Filter = "all" | "unread" | "groups" | "direct";

export default function RoomList({ activeId, onOpen, onNew }: Props) {
  const { me, rooms, users, statusOf, openDirect } = useChat();
  const [q, setQ] = useState(""); const [f, setF] = useState<Filter>("all");
  const unreadTotal = rooms.reduce((a, r) => a + (r.muted ? 0 : r.unread_count), 0);
  const list = useMemo(() => rooms.filter(r =>
    (!q || (r.name || "").includes(q) || r.members.some(m => m.name.includes(q))) &&
    (f === "all" || (f === "unread" && r.unread_count > 0) || (f === "groups" && r.type === "group") || (f === "direct" && r.type === "direct"))), [rooms, q, f]);
  const strangers = useMemo(() => q.length >= 2 ? users.filter(u => u.id !== me.id && u.name.includes(q) && !rooms.some(r => r.other_id === u.id)) : [], [q, users, rooms, me.id]);

  const preview = (r: Room) => {
    const m = r.last_message; if (!m) return "هنوز پیامی نیست";
    if (m.type === "system") return m.preview;
    const who = m.sender_id === me.id ? "شما: " : r.type === "group" ? m.sender_name.split(" ")[0] + ": " : "";
    return (m.is_deleted ? "🚫 " : "") + who + m.preview;
  };

  return (
    <aside className="cx-list">
      <div className="cx-list-head">
        <h2>گفتگوها{unreadTotal > 0 && <span className="cx-count">{unreadTotal}</span>}</h2>
        <button className="cx-btn primary icon" onClick={onNew} title="گفتگوی جدید"><Plus size={18} /></button>
      </div>
      <div className="cx-search"><Search size={15} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی گفتگو یا همکار…" /></div>
      <div className="cx-filters">
        {([["all", "همه"], ["unread", "خوانده‌نشده"], ["groups", "گروه‌ها"], ["direct", "شخصی"]] as [Filter, string][]).map(([k, l]) =>
          <button key={k} className={f === k ? "on" : ""} onClick={() => setF(k)}>{l}</button>)}
      </div>
      <div className="cx-list-body">
        {list.map(r => {
          const online = r.type === "direct" && r.other_id ? statusOf(r.other_id) : undefined;
          return (
            <button key={r.id} className={`cx-room ${activeId === r.id ? "active" : ""}`} onClick={() => onOpen(r.id)}>
              <Avatar name={r.name} src={r.avatar} size={50} group={r.type === "group"} status={online} />
              <div className="cx-room-main">
                <div className="cx-room-top"><b>{r.name}</b><time className={r.unread_count && !r.muted ? "hot" : ""}>{listTime(r.last_message?.created_at)}</time></div>
                <div className="cx-room-bot">
                  <span className={r.active_call ? "call" : ""}>{r.active_call ? <><Phone size={12} /> تماس در جریان</> : preview(r)}</span>
                  <div className="cx-room-badges">
                    {r.pinned ? <Pin size={13} /> : null}{r.muted ? <BellOff size={13} /> : null}
                    {r.mention_count > 0 && <i className="cx-badge mention"><AtSign size={11} /></i>}
                    {r.unread_count > 0 && <i className={`cx-badge ${r.muted ? "muted" : ""}`}>{r.unread_count > 99 ? "۹۹+" : r.unread_count}</i>}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
        {strangers.length > 0 && <div className="cx-list-sec">شروع گفتگو با همکاران</div>}
        {strangers.map(u => (
          <button key={u.id} className="cx-room" onClick={() => { openDirect(u.id); setQ(""); }}>
            <Avatar name={u.name} src={u.avatar} size={44} status={statusOf(u.id)} />
            <div className="cx-room-main"><div className="cx-room-top"><b>{u.name}</b></div><div className="cx-room-bot"><span>ارسال پیام جدید</span></div></div>
          </button>
        ))}
        {!list.length && !strangers.length && <Empty title={q || f !== "all" ? "موردی پیدا نشد" : "هنوز گفتگویی ندارید"} text={q || f !== "all" ? "" : "با دکمه + گفتگوی شخصی یا گروه بسازید."} />}
      </div>
    </aside>
  );
}
